package one.healthium.ricediet.presentation.feature.today

import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxScope
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.relocation.BringIntoViewRequester
import androidx.compose.foundation.relocation.bringIntoViewRequester
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.LocalContentColor
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Outline
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.Shape
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.Density
import androidx.compose.ui.unit.LayoutDirection
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.em
import androidx.compose.ui.unit.sp
import loco.GENERAL_CALORIES
import loco.GENERAL_CARBO
import loco.GENERAL_FAT
import loco.GENERAL_FIBRE
import loco.GENERAL_PROTEIN
import one.healthium.base.utils.Formatters.formatCalories
import one.healthium.base.utils.Formatters.formatGrams
import one.healthium.base.utils.orZero
import one.healthium.health_advice.ui.message.HealthAdviceMessage
import one.healthium.loco.resolve
import one.healthium.ricediet.rice
import one.healthium.today.ui.TodayUiState
import one.healthium.today.ui.calories_counter.NutritionGoalUiModel
import one.healthium.today.ui.components.TodayUserProfile
import one.healthium.ui.compose.components.AppButtonDefaults
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.ui.compose.theme.white
import org.jetbrains.compose.resources.stringResource
import androidx.compose.ui.tooling.preview.Preview
import kotlin.math.max

@Composable
fun CaloriesCounter(
    modifier: Modifier = Modifier,
    state: TodayUiState,
    onAddFoodClicked: () -> Unit,
    onLogSymptomsClicked: () -> Unit,
) {
    val bringIntoViewRequester = remember { BringIntoViewRequester() }

    Box(modifier) {
        Column(
            modifier = Modifier
                .bringIntoViewRequester(bringIntoViewRequester)
                .fillMaxWidth()
                .padding(bottom = 28.dp)
                .background(AppTheme.colors.primary, OvalShape)
                .statusBarsPadding(),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            val userProfile = state.userProfile
            if (userProfile != null) {
                CompositionLocalProvider(LocalContentColor provides white) {
                    TodayUserProfile(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(horizontal = 24.dp)
                            .padding(top = 15.dp),
                        userProfile = userProfile,
                    )
                }
            }
            HealthAdviceMessage(
                modifier = Modifier.fillMaxWidth(),
                bringIntoViewRequester = bringIntoViewRequester,
                onLogSymptomsClicked = onLogSymptomsClicked,
                style = AppTheme.textStyles.secondary_17_400,
                lineHeight = 1.5.em,
                textAlign = TextAlign.Start,
            )

            Spacer(Modifier.height(8.dp))
            CaloriesProgress(state.todayCaloriesCounter?.caloriesGoal)
            Spacer(Modifier.height(16.dp))
            Row {
                Column {
                    CarboProgress(state.todayCaloriesCounter?.carbohydratesGoal)
                    Spacer(Modifier.height(12.dp))
                    FatProgress(state.todayCaloriesCounter?.fatGoal)
                }
                Spacer(Modifier.width(38.dp))
                Column {
                    ProteinProgress(state.todayCaloriesCounter?.proteinGoal)
                    Spacer(Modifier.height(12.dp))
                    FibreProgress(state.todayCaloriesCounter?.fiberGoal)
                }
            }
            Spacer(Modifier.height(39.dp))
        }
        AddButton(onClick = onAddFoodClicked)
    }
}

@Composable
private fun CaloriesProgress(goal: NutritionGoalUiModel?) {
    val animatedProgress by animateFloatAsState(
        targetValue = goal?.progress ?: 0f,
        animationSpec = tween(durationMillis = 400, delayMillis = 200),
    )

    Box(
        modifier = Modifier.fillMaxWidth(),
        contentAlignment = Alignment.Center,
    ) {
        CircularProgressIndicator(
            modifier = Modifier.size(200.dp),
            color = AppTheme.colors.tertiary,
            trackColor = white,
            strokeWidth = 20.dp,
            strokeCap = StrokeCap.Round,
            progress = { animatedProgress },
        )
        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Text(
                text = "🍛",
                fontSize = 40.sp,
            )
            Spacer(Modifier.height(6.dp))
            Text(
                text = goal?.amount.formatCalories(addUnit = false).resolve(),
                style = AppTheme.textStyles.secondary_32_700,
                color = AppTheme.colors.onPrimary,
            )
            Text(
                text = stringResource(loco.Res.string.GENERAL_CALORIES),
                style = AppTheme.textStyles.secondary_17_400,
                color = AppTheme.colors.onPrimary,
            )
        }
    }
}

@Composable
private fun CarboProgress(goal: NutritionGoalUiModel?) {
    NutritionalGoalItem(goal, stringResource(loco.Res.string.GENERAL_CARBO))
}

@Composable
private fun ProteinProgress(goal: NutritionGoalUiModel?) {
    NutritionalGoalItem(goal, stringResource(loco.Res.string.GENERAL_PROTEIN))
}

@Composable
private fun FatProgress(goal: NutritionGoalUiModel?) {
    NutritionalGoalItem(goal, stringResource(loco.Res.string.GENERAL_FAT))
}

@Composable
private fun FibreProgress(goal: NutritionGoalUiModel?) {
    NutritionalGoalItem(goal, stringResource(loco.Res.string.GENERAL_FIBRE))
}

@Composable
private fun BoxScope.AddButton(onClick: () -> Unit) {
    val appButtonColors = AppButtonDefaults.appButtonColors()
    Surface(
        modifier = Modifier
            .size(56.dp)
            .align(Alignment.BottomCenter),
        onClick = onClick,
        shape = CircleShape,
        shadowElevation = 1.dp,
        color = appButtonColors.containerColor,
    ) {
        Box(contentAlignment = Alignment.Center) {
            Text(
                text = "+",
                style = AppTheme.textStyles.poppins32SemiBold,
                color = appButtonColors.contentColor,
            )
        }
    }
}

@Composable
private fun NutritionalGoalItem(
    goal: NutritionGoalUiModel?,
    name: String,
) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
    ) {
        CircularProgressIndicator(
            modifier = Modifier.size(28.dp),
            color = AppTheme.colors.tertiary,
            trackColor = white,
            strokeWidth = 4.dp,
            strokeCap = StrokeCap.Square,
            progress = { goal?.progress ?: 0f },
        )
        Spacer(Modifier.width(12.dp))
        Column {
            Text(
                text = goal?.amount.orZero().formatGrams().resolve(),
                style = AppTheme.textStyles.secondary_17_400,
                color = AppTheme.colors.onPrimary,
            )
            Text(
                text = name,
                style = AppTheme.textStyles.secondary_12_400,
                color = AppTheme.colors.onPrimary.copy(alpha = 0.5f),
            )
        }
    }
}

private object OvalShape : Shape {
    override fun createOutline(
        size: Size,
        layoutDirection: LayoutDirection,
        density: Density
    ): Outline {
        return Outline.Generic(
            path = Path().apply {
                val ovalSize = max(size.width, size.height)
                val radius = ovalSize * 0.9f
                addOval(
                    Rect(
                        center = Offset(size.width / 2, size.height - radius),
                        radius = radius,
                    )
                )
            }
        )
    }

}

@Composable
@Preview
private fun CaloriesCounterPreview() {
    AppTheme(rice()) {
        CaloriesCounter(
            state = TodayUiState.preview,
            onAddFoodClicked = {},
            onLogSymptomsClicked = {},
        )
    }
}